import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { MetricRecord } from "../MetricRecord";

import { metricsQueryKeys } from "./metricsQueryKeys";

/**
 * Return every metric published to a single session.
 *
 * Backs the session-detail "Metrics" tab. Unlike `useInfiniteQueryMetrics`,
 * this is a single (non-paginated) call into `v1/metrics/session/{sessionId}/`
 * with an optional `limit` query param.
 *
 * When omitted, the backend applies a default/cap of 10,000 rows. This is a
 * single non-paginated call, so it never returns more than that many metrics.
 * The limit is applied on the `GET v1/metrics/session/{sessionId}/` route
 * (packages/roboto_service/src/roboto_service/routes/metrics_router.py).
 *
 * TODO(ENG-2574): support pagination in the endpoint.
 */
export function useMetricsBySession(
  sessionId: string | undefined,
  orgId: string,
  limit?: number,
) {
  const { metricsService } = useDomainServices();

  return useQuery<MetricRecord[], Error>({
    queryKey: metricsQueryKeys.session(sessionId, orgId, limit),
    queryFn: ({ signal }) => {
      if (sessionId === undefined) {
        return Promise.reject(new Error("Invalid sessionId"));
      }
      return metricsService.listMetricsBySession(sessionId, {
        abortSignal: signal,
        orgId,
        limit,
      });
    },
    enabled: sessionId !== undefined && orgId !== undefined,
    staleTime: 60_000,
  });
}
